/**
 * One slide element, drawn.
 *
 * The dispatcher plus the elements that are not SVG: equations through KaTeX, molecules
 * through SmilesDrawer, and the three that are just typography.
 *
 * Both libraries can throw on input that passed our checks — our SMILES check is syntax, not
 * chemistry — so both are wrapped. A slide that renders eleven elements and fails on the
 * twelfth should show eleven and a note, never a blank screen in front of a class.
 */

import { useEffect, useRef, useState } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import SmilesDrawer from 'smiles-drawer';
import type { SlideElement } from './types';
import { PlotView, ChartView, DiagramView, TimelineView, NumberLineView, FlowView } from './charts';

function Broken({ what }: { what: string }) {
  return (
    <p style={{ color: 'var(--c-ink-3)', fontSize: '.9rem', fontStyle: 'italic' }}>
      This {what} could not be drawn. The rest of the slide is unaffected.
    </p>
  );
}

function EquationView({ el }: { el: Extract<SlideElement, { kind: 'equation' }> }) {
  const [html, setHtml] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    try {
      setHtml(katex.renderToString(el.latex, { displayMode: true, throwOnError: true, strict: false }));
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [el.latex]);

  if (failed) return <Broken what="equation" />;
  return (
    <figure style={{ margin: '0', textAlign: 'center' }}>
      <div role="img" aria-label={el.alt} dangerouslySetInnerHTML={{ __html: html ?? '' }} />
      {el.caption ? <figcaption style={{ fontSize: '.85rem', color: 'var(--c-ink-3)', marginTop: '.5rem' }}>{el.caption}</figcaption> : null}
    </figure>
  );
}

function MoleculeView({ el }: { el: Extract<SlideElement, { kind: 'molecule' }> }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    try {
      const drawer = new SmilesDrawer.Drawer({ width: 520, height: 340, bondThickness: 1.1 });
      SmilesDrawer.parse(
        el.smiles,
        (tree: unknown) => {
          try {
            drawer.draw(tree, canvas, 'light', false);
            setFailed(false);
          } catch {
            setFailed(true);
          }
        },
        () => setFailed(true),
      );
    } catch {
      setFailed(true);
    }
  }, [el.smiles]);

  return (
    <figure style={{ margin: 0, textAlign: 'center' }}>
      <canvas
        ref={ref}
        width={520}
        height={340}
        role="img"
        aria-label={el.alt}
        style={{ maxWidth: '100%', display: failed ? 'none' : 'inline-block' }}
      />
      {failed ? <Broken what="molecule" /> : null}
      <figcaption style={{ fontSize: '.85rem', color: 'var(--c-ink-3)', marginTop: '.4rem' }}>
        {el.caption ?? el.alt}
      </figcaption>
    </figure>
  );
}

export function SlideElementView({ el }: { el: SlideElement }) {
  switch (el.kind) {
    case 'prose':
      return (
        <div>
          {el.heading ? <h3 style={{ fontSize: '1.25rem', margin: '0 0 .6rem', fontWeight: 640 }}>{el.heading}</h3> : null}
          <ul style={{ margin: 0, paddingLeft: '1.3rem', fontSize: '1.35rem', lineHeight: 1.55 }}>
            {el.bullets.filter(Boolean).map((b, i) => <li key={i} style={{ margin: '.4rem 0' }}>{b}</li>)}
          </ul>
        </div>
      );

    case 'equation': return <EquationView el={el} />;
    case 'molecule': return <MoleculeView el={el} />;
    case 'plot': return <PlotView el={el} />;
    case 'chart': return <ChartView el={el} />;
    case 'diagram': return <DiagramView el={el} />;
    case 'timeline': return <TimelineView el={el} />;
    case 'numberLine': return <NumberLineView el={el} />;
    case 'flow': return <FlowView el={el} />;

    case 'code':
      return (
        <pre
          role="img"
          aria-label={el.alt}
          style={{
            margin: 0, padding: '1rem 1.2rem', borderRadius: '10px', overflowX: 'auto',
            background: 'var(--c-surface-2, #f6f5f2)', border: '1px solid var(--c-rule, rgba(17,20,28,.1))',
            fontSize: '1.05rem', lineHeight: 1.55,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          }}
        >
          <code>{el.source}</code>
        </pre>
      );

    case 'table':
      return (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '1.1rem' }} aria-label={el.alt}>
            <thead>
              <tr>
                {el.headers.map((h, i) => (
                  <th key={i} style={{ textAlign: 'left', padding: '.55rem .7rem', borderBottom: '2px solid var(--c-rule, rgba(17,20,28,.15))', fontWeight: 650 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {el.rows.map((r, i) => (
                <tr key={i}>
                  {(r.cells ?? []).map((c, k) => (
                    <td key={k} style={{ padding: '.55rem .7rem', borderBottom: '1px solid var(--c-rule, rgba(17,20,28,.09))' }}>{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}
